import { useState, useEffect, useRef } from 'react';
import ListingMeta from '../../Organisms/ListingMeta/ListingMeta';
import SidebarFilters from '../../Organisms/SidebarFilters';
import Pager from '../../Molecules/Pager';
import ResultsHeading from '../../Molecules/ResultsHeading';
import GooglePlacesAuto from '../../Molecules/GooglePlacesAuto';
import bem from '../../../utils/bem';
import scrollTo from '../../../utils/scrollTo';
import useWindowWidth from '../../../hooks/useWindowWidth';
import getPagedResults from '../../../utils/getPagedResults';

let isInitialized = false;

const ListingTemplate = ({
  sidebarLeft = false,
  metaSettings,
  filtersConfig = {},
  filtersState,
  setFiltersState,
  clearAllCallback,
  children,
  pagerData,
  searchCallback,
  enableAutoScroll = true,
  filtersClearAllBottom = false,
  resultsHeading,
  showSeparateLocation = false,
  showSelectedSort = false,
  mods = [],
  extra = [],
  viewMode,
  isLoading = false,
  defaultLocation,
  setSelectedLocation,
  forceModalLocation,
  filtersAnalyticsData,
}) => {
  const baseClass = 'listing';
  const listRef = useRef(null);
  const [filersActive, setFiltersActive] = useState(false);
  const { width, breakpoint } = useWindowWidth();
  const locationFilter = filtersConfig?.length
    ? filtersConfig.find((f) => f.type === 'geolocation')
    : undefined;
  const displaySwitcherView = metaSettings?.displaySwitcherView;
  const shouldShowLocation =
    locationFilter && showSeparateLocation && breakpoint.isTablet;

  const pagedItems = getPagedResults(
    pagerData.meta.pageNumber,
    pagerData.meta.pageSize,
    pagerData.meta.totalRows,
  );

  useEffect(() => {
    if (isInitialized && enableAutoScroll && !window.isStoryBook) {
      const bodyPadding =
        parseInt(document.querySelector('body').style.paddingTop, 10) || 0;
      const header = document.querySelector('.header');
      const headerHeight =
        !header || width < 960 ? 0 : parseInt(header.offsetHeight, 10);

      scrollTo(100, listRef.current.offsetTop - bodyPadding - headerHeight);
    } else if (typeof pagerData.meta.pageNumber !== 'undefined') {
      isInitialized = true;
    }
  }, [pagerData.meta.pageNumber, pagerData.meta.pageSize]);

  useEffect(() => {
    // Disable scrolling dom on mobile when filters are opened.
    const htmlClasses = document.querySelector('html').classList;
    if (filersActive && !breakpoint.isDesktop) {
      htmlClasses.add('modal-opened');
    } else {
      htmlClasses.remove('modal-opened');
    }
  }, [filersActive, breakpoint.isDesktop]);

  const onLocationChangeHandler = (l) => {
    const lat = l[0].geometry.location.lat();
    const lng = l[0].geometry.location.lng();
    setSelectedLocation(l);
    setFiltersState((prev) => ({
      ...prev,
      location: `${lat}, ${lng}`,
    }));
  };

  const onDistanceChangeHandler = (r) => {
    setFiltersState((prev) => ({ ...prev, radius: Number(r) }));
  };

  return (
    <div className={bem(baseClass, mods, '', extra)} ref={listRef}>
      {/* Filters */}
      {sidebarLeft && (
        <SidebarFilters
          formConfig={filtersConfig}
          filtersActive={filersActive}
          setFiltersActive={setFiltersActive}
          state={filtersState}
          setState={setFiltersState}
          clearAllCallback={clearAllCallback}
          searchCallback={searchCallback}
          clearAllBottom={filtersClearAllBottom}
          hideLocation={shouldShowLocation && !forceModalLocation}
          defaultLocation={defaultLocation}
          setSelectedLocation={setSelectedLocation}
          filtersAnalyticsData={filtersAnalyticsData}
        />
      )}

      {/* Content */}
      <div className={bem('content', [], baseClass)}>
        {resultsHeading && (
          <ResultsHeading
            extra={[bem('results-heading', [], baseClass)]}
            {...resultsHeading}
          />
        )}
        <div
          className={bem(
            'meta-wrapper',
            [
              shouldShowLocation ? 'with-location' : '',
              resultsHeading ? 'with-results-heading' : '',
              displaySwitcherView === 'toggle' ? 'with-toggle' : '',
            ],
            baseClass,
            [],
          )}
        >
          {shouldShowLocation && (
            <div className={bem('location-filter', [], baseClass)}>
              <p className={bem('location-label', [], baseClass)}>
                {locationFilter.label}
              </p>
              <GooglePlacesAuto
                distanceOptions={locationFilter.options}
                onPlaceSelected={(p) => onLocationChangeHandler(p)}
                onDistanceChange={(d) => onDistanceChangeHandler(d)}
                inputDefaultValue={defaultLocation}
                selectedDistance={filtersState?.radius}
              />
            </div>
          )}
          {metaSettings && (
            <ListingMeta
              {...metaSettings}
              pagedItems={pagedItems}
              setFiltersActive={setFiltersActive}
              filtersState={filtersState}
              setFiltersState={setFiltersState}
              showSelectedSort={showSelectedSort}
            />
          )}
        </div>
        {children}
        {viewMode !== 'map' && !isLoading && <Pager {...pagerData} />}
      </div>
    </div>
  );
};

export default ListingTemplate;
